/**
 * Angular `FileUploadURLComponent` parity: upload image → CDN URL → preview + **Download File** only
 * (no Copy URL / Insert / hosted URL field — see BUG #94).
 */

import { useState, type ChangeEvent } from 'react'
import { IntegratedFileSelect } from '@/components/ui/IntegratedFileSelect'
import { ConfirmDialog } from '@/components/ui/ConfirmDialog'
import { showError, showSuccess } from '@/lib/utils/notifications'
import { uploadVersionImage } from '../services/whatsNewService'
import { openVersionUploadedImageDownload } from '../utils/versionImageDownload'

const MAX_MB = 2
const LARGE_CONFIRM_MB = 1

/** Angular `selectFile` allowed MIME types (plus `image/svg+xml`). */
const ALLOWED_IMAGE_TYPES = new Set([
  'image/jpg',
  'image/jpeg',
  'image/png',
  'image/bmp',
  'image/gif',
  'image/webp',
  'image/svg',
  'image/svg+xml',
])

export function WhatsNewAttachImagePanel() {
  const [uploading, setUploading] = useState(false)
  const [uploadedUrl, setUploadedUrl] = useState<string | null>(null)
  const [largeFilePrompt, setLargeFilePrompt] = useState<{ file: File; message: string } | null>(
    null,
  )

  const runUpload = async (file: File) => {
    setUploading(true)
    setUploadedUrl(null)
    try {
      const url = await uploadVersionImage(file)
      setUploadedUrl(url)
      showSuccess('File Updated Successfully')
    } catch (err) {
      showError(err instanceof Error ? err.message : 'Image upload failed')
    } finally {
      setUploading(false)
    }
  }

  const handleFileChange = (e: ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0]
    if (!file) return

    if (!ALLOWED_IMAGE_TYPES.has(file.type)) {
      showError('Only jpg, jpeg, png, svg, bmp, gif, webp file formats are allowed')
      return
    }

    const sizeMb = file.size / 1024 / 1024
    if (sizeMb > MAX_MB) {
      showError('File Size Exceeds Allowable Limit of 2 MB.')
      return
    }

    if (sizeMb > LARGE_CONFIRM_MB) {
      const line = `${file.name} - ${sizeMb.toFixed(2)} MB`
      setLargeFilePrompt({
        file,
        message: `Please confirm, really want to continue to upload a large file\n\n${line}`,
      })
      return
    }

    void runUpload(file)
  }

  return (
    <div className="space-y-2">
      <div className="mb-1 block text-sm font-medium text-slate-700">Attach Image</div>
      <IntegratedFileSelect
        displayName=""
        accept="image/jpeg,image/png,image/bmp,image/gif,image/webp,image/svg+xml,.jpg,.jpeg,.png,.gif,.webp,.svg,.bmp"
        emptyLabel="Choose file…"
        browseLabel="Select file"
        disabled={uploading}
        onChange={handleFileChange}
        data-testid="whats-new-version-image"
      />

      {uploadedUrl ? (
        <div className="space-y-2 rounded-lg border border-slate-200 bg-slate-50/80 p-2 sm:p-3">
          <div className="flex flex-col items-start gap-3 sm:flex-row sm:flex-wrap sm:items-start">
            <img
              src={uploadedUrl}
              alt=""
              className="h-[100px] max-w-full rounded border border-slate-200 bg-white object-contain"
            />
            <button
              type="button"
              className="text-sm font-medium text-[#1597ff] underline hover:no-underline"
              data-testid="whats-new-download-uploaded-image"
              onClick={() => void openVersionUploadedImageDownload(uploadedUrl)}
            >
              Download File
            </button>
          </div>
        </div>
      ) : null}

      <ConfirmDialog
        isOpen={largeFilePrompt !== null}
        title="Large File"
        message={largeFilePrompt?.message ?? ''}
        messageLayout="largeFile"
        primaryLabel="Upload"
        onPrimary={() => {
          const f = largeFilePrompt?.file
          setLargeFilePrompt(null)
          if (f) void runUpload(f)
        }}
        secondaryLabel="Cancel"
        onSecondary={() => setLargeFilePrompt(null)}
        onDismiss={() => setLargeFilePrompt(null)}
        primaryFirst
      />
    </div>
  )
}
